import React, { useContext, useEffect } from 'react';
import { ObjektVyContext } from '../ObjektVy/ObjektVy';
import { VisningsTypContext } from '../VisningsTypContext/VisningsTypContext';
import { RutinSettingsContext } from '../../RutinAvailabilityContext/RutinAvailabilityContext';
import { DetaljurvalContextProvider } from '../DetaljUrvalContext/DetaljUrvalContext';
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';
import { EDControlledExpansionPanel } from '../../../../../../BaseComponents/Accordion/EDExpansionPanel';
import {
  FLOATING_MENU_CLEARANCE,
  useIsFloatingMenuVisible,
} from '../../../../../../Components/FloatingBottomArea/FloatingMenu';

interface IProps {
  caption: string;
  columns?: number;
  sidNr: number;
  collectionName: string;
  /** Använder en container anpassad för sidor med en lista (bör nog ses över vad den egentligen styr) */
  disableMasonry?: boolean;
  leftMenuPresent?: boolean;
  fillPageSmall?: boolean;
  /** Ingen padding på small-skärmar */
  smallScreenNoPadding?: boolean;
  hidden?: boolean;
  forceActive?: boolean;
  /** Ingen padding eller margin alls */
  noPaddingMargin?: boolean;
  disableFloatingMenuPadding?: boolean;
}

interface IObjektVySidaBakgrundProps {
  fullPage?: boolean;
  hideOverflow?: boolean;
  listPadding?: boolean;
  sideBySide?: boolean;
  smallScreenNoPadding?: boolean;
  noPaddingMargin?: boolean;
  disableFloatingMenuPadding?: boolean;
}

const ObjektVySidaBakgrund: React.FunctionComponent<
  React.PropsWithChildren<IObjektVySidaBakgrundProps>
> = ({
  children,
  fullPage,
  hideOverflow,
  listPadding,
  sideBySide,
  smallScreenNoPadding,
  noPaddingMargin,
  disableFloatingMenuPadding,
}) => {
  const isFloatingMenuVisible = useIsFloatingMenuVisible();
  /**
   * Replace a `padding: <shorthand>` declaration with longhand top/left/right
   * (and bottom only when the floating menu is not reserving space) so that a
   * later `paddingBottom` rule isn't reset by the shorthand.
   */
  const splitPadding = (
    top: number | string,
    x: number | string,
    bottom: number | string = top
  ) =>
    isFloatingMenuVisible
      ? { paddingTop: top, paddingLeft: x, paddingRight: x }
      : {
          paddingTop: top,
          paddingLeft: x,
          paddingRight: x,
          paddingBottom: bottom,
        };

  const addPaddingForFloatingMenu =
    isFloatingMenuVisible && !disableFloatingMenuPadding && !hideOverflow;
  return (
    <EDBox
      sx={theme => ({
        flex: 1,
        overflowX: 'hidden',
        boxSizing: 'border-box',
        backgroundColor: theme.palette.background.default,
        ...(fullPage
          ? {
              ...(noPaddingMargin
                ? { padding: 0 }
                : splitPadding(theme.spacing(3), theme.spacing(5))),
              [theme.breakpoints.down('xl')]: {
                ...(noPaddingMargin
                  ? { padding: 0 }
                  : splitPadding(theme.spacing(2), theme.spacing(2))),
              },
              [theme.breakpoints.down('md')]: {
                height: '100%',
              },
            }
          : {}),
        ...(smallScreenNoPadding
          ? {
              [theme.breakpoints.down('md')]: {
                padding: `0 0 ${
                  addPaddingForFloatingMenu ? FLOATING_MENU_CLEARANCE : 0
                } 0`,
              },
            }
          : {}),
        ...(hideOverflow
          ? {
              height: '100%',
              overflow: 'hidden',
              paddingBottom: 0,
            }
          : {}),
        ...(listPadding
          ? {
              paddingLeft: noPaddingMargin ? 0 : theme.spacing(0),
            }
          : {}),
        ...(sideBySide
          ? {
              ...(noPaddingMargin
                ? { padding: 0 }
                : splitPadding(theme.spacing(1), theme.spacing(1))),
              margin: noPaddingMargin ? 0 : theme.spacing(-1),
              height: '100%',
            }
          : {}),
        ...(addPaddingForFloatingMenu && {
          paddingBottom: FLOATING_MENU_CLEARANCE,
        }),
      })}
    >
      {children}
    </EDBox>
  );
};

const ObjektVySida: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const [objektVyContext, dispatch] = useContext(ObjektVyContext);
  const { activeRoutine } = useContext(RutinSettingsContext);
  const visningsTypContext = useContext(VisningsTypContext);
  const sidIdx = props.sidNr - 1;
  const sida = objektVyContext.sidor[sidIdx];
  const visiblePagesCount = objektVyContext.sidor.filter(
    vySida => !vySida.hidden
  ).length;
  const hidden = Boolean(props.hidden ?? sida?.hidden);
  const aktiv = Boolean(sida?.aktiv) || Boolean(props.forceActive);
  const useAccordion =
    objektVyContext.accordionMode && visiblePagesCount > 1 && !hidden;

  useEffect(
    () =>
      dispatch({
        type: 'setCaption',
        caption: props.caption,
        collectionName: props.collectionName,
        sidNr: props.sidNr,
        hidden: !!props.hidden,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.caption, props.collectionName, props.sidNr, !!props.hidden]
  );

  if ((hidden && !aktiv) || (!aktiv && !useAccordion)) {
    return null;
  }

  const pageContent = (
    <DetaljurvalContextProvider
      collectionName={props.collectionName}
      rutinID={activeRoutine?.routine.rutinID || 0}
    >
      <ObjektVySidaBakgrund
        fullPage={
          !objektVyContext.sidaVidSida && !visningsTypContext.showDetailsRight
        }
        listPadding={visningsTypContext.showDetailsRight}
        sideBySide={objektVyContext.sidaVidSida}
        smallScreenNoPadding={props.smallScreenNoPadding}
        noPaddingMargin={props.noPaddingMargin}
        disableFloatingMenuPadding={props.disableFloatingMenuPadding}
      >
        {props.children}
      </ObjektVySidaBakgrund>
    </DetaljurvalContextProvider>
  );

  if (useAccordion) {
    return (
      <EDControlledExpansionPanel
        disableDetailsPadding
        label={props.caption ?? sida?.caption ?? '-'}
        expanded={Boolean(sida?.aktiv)}
        keepMounted
        onChange={(_, expanded) => {
          const pageIdx = props.sidNr - 1;
          const activePageIndices = objektVyContext.sidor.reduce<number[]>(
            (indices, vySida, idx) =>
              vySida.aktiv ? [...indices, idx] : indices,
            []
          );
          const nextActivePageIndices = expanded
            ? activePageIndices.includes(pageIdx)
              ? activePageIndices
              : [...activePageIndices, pageIdx]
            : activePageIndices.filter(idx => idx !== pageIdx);

          dispatch({
            type: 'setAktuellaVyer',
            aktuellaVyer: nextActivePageIndices,
          });
        }}
      >
        {pageContent}
      </EDControlledExpansionPanel>
    );
  }

  return pageContent;
};

export default ObjektVySida;
